import { ClientTagKey, isClientTagKey } from "intake_shared/src/client_tags";
import { AppSortOrder, ClientStatus } from "intake_shared/src/types";
import type { TableSortBy } from "../../bits/Table/Table";

export interface IntakeQueueQueryState {
    assignees: string[];
    tags?: ClientTagKey[];
    page: number;
    status?: ClientStatus;
    sortKey?: string;
    sortDirection?: AppSortOrder;
    queueOrder?: AppSortOrder;
    tableSort?: TableSortBy;
}

export interface IntakeQueueNavigationState {
    intakeQueue?: {
        searchByEmail: string;
        searchByLastName: string;
        searchByPhoneNo: string;
        searchByDob: string | null;
        hasSearched: boolean;
    };
}

export interface IntakeQueueSearchState {
    searchByEmail: string;
    searchByLastName: string;
    searchByPhoneNo: string;
    searchByDob?: Date | null;
}

export function getIntakeQueueNavigationState(
    state: unknown
): IntakeQueueNavigationState {
    return (state ?? {}) as IntakeQueueNavigationState;
}

export function getRestoredIntakeQueueDate(value?: string | null) {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function serializeIntakeQueueSearchState(
    searchState: IntakeQueueSearchState
) {
    return {
        ...searchState,
        searchByDob: searchState.searchByDob?.toISOString() ?? null,
    };
}

export function getIntakeQueueQueryState(search: string): IntakeQueueQueryState {
    const searchParams = new URLSearchParams(search);
    const page = Number(searchParams.get("page"));
    const sortDirection = searchParams.get("sortDirection");
    const queueOrder = searchParams.get("queueOrder");
    const tableSortKey = searchParams.get("tableSortKey");
    const tableSortDirection = searchParams.get("tableSortDirection");

    return {
        tags: Array.from(
            new Set((searchParams.get("tags") ?? "").split(","))
        ).filter(isClientTagKey),
        assignees: (searchParams.get("assignees") ?? "").split(",").filter(Boolean),
        page: Number.isInteger(page) && page > 0 ? page : 1,
        status: (searchParams.get("status") as ClientStatus) || undefined,
        sortKey: searchParams.get("sortKey") || undefined,
        sortDirection:
            sortDirection === AppSortOrder.ASC || sortDirection === AppSortOrder.DESC
                ? sortDirection
                : undefined,
        queueOrder:
            queueOrder === AppSortOrder.ASC || queueOrder === AppSortOrder.DESC
                ? queueOrder
                : undefined,
        tableSort: tableSortKey
            ? {
                  id: tableSortKey,
                  desc: tableSortDirection === AppSortOrder.DESC,
              }
            : undefined,
    };
}

export function updateIntakeQueueSearch(
    search: string,
    updates: Record<string, string | undefined>
) {
    const searchParams = new URLSearchParams(search);

    Object.entries(updates).forEach(([key, value]) => {
        if (value) {
            searchParams.set(key, value);
        } else {
            searchParams.delete(key);
        }
    });

    return searchParams.toString();
}
